<template>
  <div class="f-h action-plans" :class="actionPlansClass">
    <FiltersZone v-if="!asWidget" :modal-title="$t('message.OPTIONS')">
      <div slot="shortcuts" class="v-centerer">
        <h2 v-if="asComponent" class="basic-sub-title action-plans__page-title">
          {{ pageTitle }}
        </h2>
        <button
          class="generic-border generic-border--small-text-btn action-plans__archive-all"
          @click="archiveAllA"
        >
          {{ $t('message.ALL_A_ACTION_COMPLETED') }}
        </button>
      </div>
      <ComponentsList
        :value="actOptions"
        @edit="paramsEdit"
      />
    </FiltersZone>
    <div ref="plansContainer" class="f-h-below-filters action-plans__plans-container">
      <!-- Securing actions section -->
      <div
        v-if="actOptDisplayOngoingSecuringActions && (compPlans.length > 0 || !plansLoading)"
        class="action-plans__plans__plan action-plans__plans__plan--securing generic-border"
      >
        <div class="action-plans__plans__title-container">
          <h3>
            {{ $t('message.SECURING_ACTIONS') }}
          </h3>
        </div>
        <div class="action-plans__plans__plan__actions">
          <ActionItem
            v-for="action in securingActions"
            :key="`${action.id}`"
            :value="action"
            :all-users="allTeams"
            disabled
            @deleteClick="confirmDeleteAction(action)"
            @actionClick="securingActionClicked(action, true)"
          />
        </div>
      </div>
      <div class="action-plans__plans">
        <div
          v-for="(plan, pInd) in compPlans"
          :key="pInd"
          class="action-plans__plans__plan generic-border"
        >
          <div class="action-plans__plans__title-container">
            <h3 class="v-centerer">
              <span>{{ decodeHTML(plan.name) }}</span>
              <span v-if="plan.team !== currentTeam.id">{{ `&nbsp;${decodeHTML(plan.teamName)}` }}</span>
            </h3>
            <span class="count">
              {{ fillIndicator(plan) }}
            </span>
          </div>
          <div
            :id="`actions-plan-${pInd}`"
            ref="plans"
            class="action-plans__plans__plan__actions plan-action-dropzone"
          >
            <PlanActionItem
              v-for="action in plan.actions"
              :ref="`action${action.id}`"
              :key="`plan-${plan.id}-action-${action.id}`"
              :data-id="action.id"
              :data-plan="plan.id"
              :action="action"
              :all-users="$store.getters.usersPerTeam[`${action.affectTeam}`]"
              :filters="plan.support == 0 && plan.team === currentTeam.id ? actOptLeader : null"
              :can-drag="plan.support == 0 && !preventDraggingActions"
              :plan="plan"
              :n-lines="currentTeam.planActionLines"
              :is-dragging="isDragging"
              @pdcaEdit="refresh"
              @leaderEdit="refresh"
            />
          </div>
        </div>
      </div>
    </div>
    <div id="plan-drop-zone">
      <div class="plan-backto-pending v-centerer inline f-h">
        <div>{{ $t("message.REMOVE_FROM_PLAN") }}</div>
      </div>
      <div class="plan-delete generic-border v-centerer inline f-h">
        <div>{{ $t("message.DELETE") }}</div>
      </div>
      <div class="plan-completed v-centerer inline f-h">
        <div>{{ $t("message.ACTION_COMPLETED") }}</div>
      </div>
    </div>
    <!-- Team picker when sending an action to a support plan -->
    <AdvancedTeamPicker
      v-if="supportData.plan != null"
      :value="[]"
      display-all-teams
      invisible
      ok-button
      single-value
      edit-mode-always-on
      @input="suppTeamClicked"
      @endEdit="suppTeamClicked(null)"
    />
  </div>
</template>

<script>
import { cloneDeep, isEqual } from 'lodash';
import toastr from 'toastr';
import $ from 'jquery';
import { mapState } from 'vuex';
// Vuex
import {
  FETCH_PENDING_ACTIONS_NUMBER,
  FETCH_TEAMS,
  FETCH_USERS,
  FETCH_ACTION_PLANS,
  FETCH_MANAGED_ACTION_PLANS,
  ASSIGN_ACTION,
  RETURN_ACTION,
  FETCH_ONGOING_SECURING_ACTIONS,
  FETCH_ONGOING_QUICK_ACTIONS,
} from '@/store/action-types';
import {
  GET_USERS,
  GET_ACTION_PLANS,
  GET_MANAGED_ACTION_PLANS,
  GET_ONGOING_SECURING_ACTIONS,
  GET_TEAMS,
  GET_ONGOING_QUICK_ACTIONS,
} from '@/store/mutation-types';
import { DELETE_ACTION } from '@/store/modules/problem/action-types';
// Components
import ActionItem from '@/components/action-item.vue';
import AdvancedTeamPicker from '@/components/advanced-team-picker.vue';
import ComponentsList from '@/components/components-list.vue';
import mixinActionsOptions from '@/mixins/page-options/actions.vue';
import FiltersZone from '@/components/filters-zone.vue';
import PlanActionItem from '@/components/plan-action-item.vue';
// Mixins
import mixinCommonColors from '@/mixins/common-colors.vue';
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Socket handling utils
import SocketMessages from '@/utils/classes/SocketMessages';
// Other utils
import IAAnalyser from '@/utils/classes/IAAnalyser';
import IAHelp from '@/utils/classes/IAHelp';
import IAHelpStep from '@/utils/classes/IAHelpStep';
import {
  decodeHTML,
  calert,
} from '@/utils/main';
import { isActionCompleted } from '@/utils/action-utils';

export default {

  name: 'Actions',

  components: {
    ActionItem,
    AdvancedTeamPicker,
    ComponentsList,
    FiltersZone,
    PlanActionItem,
  },

  mixins: [mixinSocketHandler, mixinActionsOptions, mixinCommonColors],

  props: {
    asWidget: Boolean,
    planIdsFromWidget: {
      type: String,
      default: () => '',
    },
    landscape: Boolean,
    preventDraggingActions: Boolean,
    asComponent: Boolean,
  },

  data() {
    return {
      planActionIdSep: '-',
      smPlanActionId: 'sm-plan-action-',
      demoPlans: [],
      preventRefresh: false,
      refreshPending: false,
      destroyed: false,
      /** Support assignment */
      supportData: { plan: null, action: null, fromPlan: null },
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.ACTION_PLANS'),
      iaAnalysisText: this.$t('message.ANALYSING'),
      iaTeachText: this.$t('sensei.TEACHER_TEXT'),
      iaTeachBtns: [this.teacherBtn('ST_LT'), this.teacherBtn('PDCA')],
      isDragging: false,
    };
  },

  computed: {
    compPlans() {
      if (this.demoMode) {
        return this.demoPlans;
      }
      // Apply filters : Bring actions to top if leader is selected
      let plans = [...this.plans];
      if (!this.managedPlansLoading && this.asWidget) {
        this.managedPlans.forEach((team) => {
          if (Number(team.id) !== this.currentTeam.id) {
            plans.push(
              ...team.plans
                .filter(ap => this.actOptDisplayedActionPlans.includes(ap.id))
                .map(ap => ({ ...ap, teamName: team.name })),
            );
          }
        });
      }
      plans = cloneDeep(plans).filter(ap => this.actOptDisplayedActionPlans.includes(ap.id));
      plans.forEach((plan) => {
        if (plan.support === 0) {
          (plan.actions || []).sort((a, b) => {
            if (this.actOptLeader.includes(a.leader || 0) && !this.actOptLeader.includes(b.leader || 0)) {
              return -1;
            }
            if (this.actOptLeader.includes(b.leader || 0) && !this.actOptLeader.includes(a.leader || 0)) {
              return 1;
            }
            return 0;
          });
        }
        const { desc, value } = this.actOptSortBy;
        plan.actions = plan.actions.map((action) => {
          action.addedSortable = this.$moment(action.added).format('YYYYMMDDHHmmss');
          action.deadlineSortable = this.$moment(action.deadline).format('YYYYMMDDHHmmss');
          return action;
        });
        switch (value) {
          case 0:
            return !desc
              ? (plan.actions || []).sort((a, b) => a.name.localeCompare(b.name))
              : (plan.actions || []).sort((a, b) => b.name.localeCompare(a.name));
          case 1:
            return !desc
              ? (plan.actions || []).sort((a, b) => a.addedSortable - b.addedSortable)
              : (plan.actions || []).sort((a, b) => b.addedSortable - a.addedSortable);
          case 2:
            return !desc
              ? (plan.actions || []).sort((a, b) => a.deadlineSortable - b.deadlineSortable)
              : (plan.actions || []).sort((a, b) => b.deadlineSortable - a.deadlineSortable);
          case 3:
            return !desc
              ? (plan.actions || []).sort((a, b) => a.impact - b.impact)
              : (plan.actions || []).sort((a, b) => b.impact - a.impact);
          default:
            return (plan.actions || []);
        }
      });
      return plans;
    },
    /** Boolean: Return true if you are on mobile landscape */
    isMobileLandscape() {
      return this.$store.state.device.isSmartphoneLandscape;
    },
    /** Array: Array of all the actions that are completed */
    allCompletedActions() {
      return this.plans.reduce((actions, plan) => {
        return [...actions, ...(plan.actions.filter(action => isActionCompleted(action)))];
      }, []);
    },
    ...mapState({
      plans: state => state[GET_ACTION_PLANS.stateKey] || [],
      plansLoading: state => state[GET_ACTION_PLANS.loadingKey],
      managedPlans: state => state[GET_MANAGED_ACTION_PLANS.stateKey] || [],
      managedPlansLoading: state => state[GET_MANAGED_ACTION_PLANS.loadingKey],
      securingActions: state => state[GET_ONGOING_SECURING_ACTIONS.stateKey] || [],
      securingActionsLoading: state => state[GET_ONGOING_SECURING_ACTIONS.loadingKey],
      quickActions: state => state[GET_ONGOING_QUICK_ACTIONS.stateKey] || [],
      quickActionsLoading: state => state[GET_ONGOING_QUICK_ACTIONS.loadingKey],
      allTeams: state => state[GET_TEAMS.stateKey] || [],
      teamUsers: state => state[GET_USERS.stateKey] || [],
    }),
    // ============
    // Query params
    // ============
    actOptSortBy() {
      const actionsSortBy = Number(this.$route.query.actionsSortBy);
      return {
        value: Number.isNaN(actionsSortBy) ? 1 : actionsSortBy,
        desc: this.$route.query.actionsSortDesc === 'true',
      };
    },
    actOptLeader() {
      const queryIds = this.$route.query.leader;
      if (queryIds === '') {
        return [];
      }
      return typeof queryIds === 'undefined'
        ? this.actOptUserItems.map(t => t.id)
        : queryIds.split(',').map(e => Number(e));
    },
    /** Boolean: If true, add a fake action plan containing only securing action items */
    actOptDisplayOngoingSecuringActions() {
      return this.$route.query.displayOngoingSecuringActions === 'true';
    },
    /** Array: IDs of the currently displayed action plans */
    actOptDisplayedActionPlans() {
      const planIds = this.asWidget ? this.planIdsFromWidget : this.$route.query.displayedActionPlans;
      if (planIds === '') {
        return [];
      }
      return typeof planIds === 'undefined'
        ? this.actOptActionPlans.map(ap => ap.id)
        : planIds.split(',').map(e => Number(e));
    },
    /** Object: Classes applied to the main element */
    actionPlansClass() {
      return {
        'action-plans--landscape': this.landscape,
        'action-plans--as-widget': this.asWidget,
        'action-plans--as-component': this.asComponent,
      };
    },
  },

  watch: {
    teamUsers() {
      this.$nextTick(this.setupDragAndDrop);
    },
    actOptDisplayOngoingSecuringActions(display) {
      if (display) {
        this.refresh();
      }
    },
    isActionCreationDisplayed() {
      // Refresh the action plans if an action has been updated
      if (!this.isActionCreationDisplayed) {
        this.refresh();
      }
    },
    isMobileLandscape(e) {
      if (e) {
        this.destroyDragAndDrop();
      }
    },
  },

  created() {
    this.$store.dispatch(FETCH_TEAMS);
    this.$store.dispatch(FETCH_USERS);
    this.$nextTick(() => {
      $(window).on(`resizeEnd.${this.uuid}`, this.refresh);
      this.refresh();
      this.registerSocketHandlers();
    });
  },

  beforeDestroy() {
    this.destroyed = true;
    this.destroyDragAndDrop();
  },

  methods: {
    decodeHTML,
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.CC_MSGS.ACTION_PLANS, () => {
        this.refresh();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PENDING_AND_PLANS, () => {
        this.refresh();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PROBLEMS_AND_PLANS, () => {
        this.refresh();
      });
    },
    refresh() {
      if (this.preventRefresh) {
        this.refreshPending = true;
        return;
      }
      // Refresh the data
      this.$store.dispatch(FETCH_ACTION_PLANS, { actions: true }).then(() => {
        setTimeout(this.setupDragAndDrop, 1);
      });
      /**
       * If you are using the action plans widget it gonna fetch managed teams action plans
       * then it gonna emit all plans
       */
      if (this.asWidget) {
        if (!this.managedPlansLoading && this.currentTeam.hierarchyLevel > 0) {
          this.$store.dispatch(FETCH_MANAGED_ACTION_PLANS, { teams: true });
        }
      }
      // Refresh securing actions if needed
      if (this.actOptDisplayOngoingSecuringActions && !this.securingActionsLoading) {
        this.$store.dispatch(FETCH_ONGOING_SECURING_ACTIONS);
      }
      if (!this.quickActionsLoading) {
        this.$store.dispatch(FETCH_ONGOING_QUICK_ACTIONS);
      }
    },
    paramsEdit(option) {
      const query = Object.assign({}, this.$route.query);
      if (option.key === 'actionsSortBy') {
        query.actionsSortBy = option.value.value;
        query.actionsSortDesc = String(option.value.desc);
      } else if (option.key === 'leader') {
        query.leader = option.value.join(',');
      } else if (option.key === 'displayOngoingSecuringActions') {
        query.displayOngoingSecuringActions = option.value ? 'true' : undefined;
      } else if (option.key === 'displayedActionPlans') {
        query.displayedActionPlans = option.value.join(',');
      }
      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }
    },
    /** Returns displayed fill indicator for an action plan */
    fillIndicator(plan) {
      return `${(plan.actions || []).length} / ${plan.support === 1 ? '∞' : plan.maxActions}`;
    },
    /** Move an action back to pending */
    backToPending(action, plan) {
      this.$store.dispatch(ASSIGN_ACTION, { planId: 0, fromPlanId: plan.id, action })
        .then((data) => {
          if (typeof data === 'object' && data.id) {
            toastr.remove();
            toastr.success(this.$t('message.ACTION_REMOVED'));
            this.sendSocketMessage(SocketMessages.CC_MSGS.PENDING_AND_PLANS);
          } else {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
          this.refresh();
        });
    },
    /** Complete an action */
    complete(action, plan) {
      this.$http.post('/server/actionPlans/complete.php', { plan: 0, fromPlan: plan.id, id: action.id })
        .then((response) => {
          const { data } = response;
          if (!isNaN(data)) {
            toastr.success(this.$t('message.ACTION_COMPLETED'));
            if (action.problem) {
              this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PROBLEMS_AND_PLANS, id: action.problem });
              this.sendSocketMessage({
                msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
                socketCompany: 1,
                id: action.problem,
              });
            } else {
              this.sendSocketMessage(SocketMessages.CC_MSGS.ACTION_PLANS);
            }
            if (data > 1) {
              // Then the action came from another team (as a support action). And data is the id of this team
              this.sendSocketMessage({ msg: SocketMessages.NOTIF_MSG, team: data });
            }
            this.$store.dispatch(FETCH_PENDING_ACTIONS_NUMBER, true);
            this.refresh();
          } else {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
        });
    },
    /** Archive all actions where PDCA is set to Act or completed */
    archiveAllA() {
      calert(this.$t('message.ALL_A_ACTION_COMPLETED_CONFIRM'), [
        {
          title: this.$t('message.ARCHIVE'),
          action: () => {
            if (this.allCompletedActions.length > 0) {
              // Promise array with each completion
              const completions = this.allCompletedActions.map(action => new Promise((resolve, reject) => {
                this.$http.post('/server/actionPlans/complete.php', { plan: 0, fromPlan: action.plan, id: action.id })
                  .then(({ data }) => {
                    if (!isNaN(data) && data > 1) {
                      // If the action came from another team (support action), notify the team
                      this.sendSocketMessage({ msg: SocketMessages.NOTIF_MSG, team: data });
                    }
                    resolve();
                  });
              }));
              Promise.all(completions).then(() => {
                toastr.success(this.$t('message.ACTION_COMPLETED'));
                this.sendSocketMessage(SocketMessages.CC_MSGS.ACTION_PLANS);
                this.refresh();
              });
            }
          },
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** Dropped and confirmed on a plan */
    assign(action, plan, fromPlan) {
      // Handle support plan
      if (plan.support === 1) {
        this.supportData = { plan, action, fromPlan };
        return;
      }
      // Classic plan
      this.$store.dispatch(ASSIGN_ACTION, { action, planId: plan.id, fromPlanId: fromPlan.id }).then(this.refresh);
    },
    /** Send request to assign the action to the selected team */
    suppTeamClicked(teams) {
      if (teams == null || teams.length === 0) {
        this.supportData.plan = null;
        return;
      }
      const o = this.supportData;
      const params = { action: o.action, planId: o.plan.id, fromPlanId: o.fromPlan.id, supportTeamId: teams[0] };
      this.$store.dispatch(ASSIGN_ACTION, params).then(this.refresh);
      this.supportData.plan = null;
    },
    confirmDeleteAction(action) {
      calert(this.$t('message.CONFIRM_DELETE_ACTION'), [
        {
          title: this.$t('message.DELETE_ACTION'),
          action: () => { this.delete(action); },
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    /** Completely remove an action from the database */
    delete(action) {
      this.$store.dispatch(`problem/${DELETE_ACTION}`, { id: action.id }).then(({ data }) => {
        if (data === 1) {
          this.sendSocketMessage(SocketMessages.CC_MSGS.ACTION_PLANS);
          toastr.success(this.$t('message.ACTION_DELETED'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
        this.refresh();
      });
    },
    /** Allow the user to return an action */
    promptReturnAction(action) {
      calert(`${this.$t('message.ACTION_FROM_OTHER_TEAM')}<br/>${this.$t('message.CANT_DELETE_BUT_RETURN')}`, [
        { title: this.$t('message.RETURN_ACTION'), action: () => { this.returnAction(action); } },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** If the action was assigned from another team, return the action to the previous team */
    returnAction(action) {
      this.$store.dispatch(RETURN_ACTION, { id: action.id, team: action.fromTeam }).then(this.refresh);
    },
    setupDragAndDrop() {
      if (this.destroyed || this.isMobileLandscape) {
        return;
      }
      $('#plan-drop-zone').droppable({
        accept: '.plan-action-item',
        drop() {
          $('.plan-action-dropzone.ui-droppable').droppable('enable');
        },
        over() {
          $('.plan-action-dropzone.ui-droppable').droppable('disable');
          const $hovered = $('.plan-action-dropzone.ui-droppable.ui-droppable-hover');
          $hovered.addClass('ui-droppable-pending-hover');
          $hovered.removeClass('ui-droppable-hover');
        },
        out() {
          $('.plan-action-dropzone.ui-droppable').droppable('enable');
          const $hovered = $('.plan-action-dropzone.ui-droppable.ui-droppable-pending-hover');
          $hovered.removeClass('ui-droppable-pending-hover');
          $hovered.addClass('ui-droppable-hover');
        },
      });
      // Drop to remove from plans
      $('.plan-backto-pending').droppable({
        addClasses: false,
        accept: '.action-plans .plan-action-item',
        drop: (e, ui) => {
          const plan = this.plans.find(p => p.id === Number(ui.draggable[0].dataset.plan));
          const action = plan.actions.find(p => p.id === Number(ui.draggable[0].dataset.id));
          if (action && plan) {
            calert(this.$t('message.REMOVE_FROM_PLAN_CONFIRM'),
              [{ title: this.$t('message.REMOVE_ACTION'), action: () => { this.backToPending(action, plan); } },
                { title: this.$t('message.CANCEL') }]);
          }
        },
      });
      // Drop to complete an action
      $('.plan-completed').droppable({
        addClasses: false,
        accept: '.action-plans .plan-action-item',
        drop: (e, ui) => {
          const plan = this.plans.find(p => p.id === Number(ui.draggable[0].dataset.plan));
          const action = plan.actions.find(a => a.id === Number(ui.draggable[0].dataset.id));
          if (action && plan) {
            calert(this.$t('message.ACTION_COMPLETED_CONFIRM'),
              [{ title: this.$t('message.COMPLETE_ACTION'), action: () => { this.complete(action, plan); } },
                { title: this.$t('message.CANCEL') }]);
          }
        },
      });
      // Drop to delete an action
      $('.plan-delete').droppable({
        addClasses: false,
        accept: '.action-plans .plan-action-item',
        drop: (e, ui) => {
          const plan = this.plans.find(p => p.id === Number(ui.draggable[0].dataset.plan));
          const action = plan.actions.find(p => p.id === Number(ui.draggable[0].dataset.id));
          if (action && plan) {
            // This team is not the owner of the action
            if (this.currentTeam.id !== Number(action.reportTeam)) {
              this.promptReturnAction(action);
              return;
            }
            this.confirmDeleteAction(action);
          }
        },
      });
      // Scrollable div
      let $clone;
      const container = this.$refs.plansContainer; // $('.action-plans')[0];
      let originalPosition;
      const maxScrollWidth = container.scrollWidth;
      const maxScrollHeight = container.scrollHeight;
      let corrected = false;
      // Ids for droppable
      const acceptIds = [];
      const plen = this.plans.length;
      for (let i = 0; i < plen; i += 1) {
        acceptIds.push(`#actions-plan-${i} .plan-action-item`);
      }
      const plansContainer = $(container);
      // Setup main measurement variables
      const containerRect = container.getBoundingClientRect();
      // Space between left of the window and container
      const leftScreenGap = containerRect.left;
      // Space between top of the window and container
      const topScreenGap = containerRect.top;
      // Margin between the mouse cursor and the edge of the container before scrolling should start
      const scrollOffset = 15;
      this.plans.forEach((p, i) => {
        // Allow dragging an action item to all plans except the one it is currently attached to
        $(`#actions-plan-${i} .plan-action-item`).draggable({
          handle: '.item-handle',
          addClasses: false,
          refreshPositions: true,
          distance: 15,
          start: (e, ui) => {
            this.preventRefresh = true;
            $(ui.helper).css('width', `${e.target.offsetWidth}px`);
          },
          stop: () => {
            this.preventRefresh = false;
            if (this.refreshPending) {
              this.refresh();
            }
            this.isDragging = false;
          },
          drag: (e, ui) => {
            corrected = false;
            this.isDragging = true;
            // Scroll correction to prevent the container from scrolling indefinitely when draging an action.
            // Scroll on left / right when dragging (if landscape display is disabled)
            if (!this.landscape) {
              if (ui.position.left <= leftScreenGap) {
                ui.position = { top: ui.position.top - topScreenGap, left: 0 };
                plansContainer.scrollLeft(plansContainer.scrollLeft() - scrollOffset);
                corrected = true;
              }
              if (ui.position.left + topScreenGap >= maxScrollWidth) {
                ui.position = { top: ui.position.top - topScreenGap, left: maxScrollWidth - $(e.target).width() };
                plansContainer.scrollLeft(plansContainer.scrollLeft() + scrollOffset);
                corrected = true;
              }
              if (ui.position.top + $(e.target).height() - topScreenGap >= maxScrollHeight && !this.asWidget) {
                ui.position = { top: maxScrollHeight - $(e.target).height() };
                corrected = true;
              }
            }
            // Scroll on top / bottom when dragging (if landscape display is enabled)
            if (this.landscape) {
              if (ui.position.top + topScreenGap >= maxScrollHeight) {
                ui.position = { top: ui.position.top - topScreenGap, left: ui.position.left - leftScreenGap };
                plansContainer.scrollTop(plansContainer.scrollTop() + scrollOffset);
                corrected = true;
              }
              if (ui.position.top <= topScreenGap) {
                ui.position = { top: ui.position.top - topScreenGap, left: ui.position.left - leftScreenGap };
                plansContainer.scrollTop(plansContainer.scrollTop() - scrollOffset);
                corrected = true;
              }
            }
            if (!corrected) {
              ui.position = { top: ui.position.top - topScreenGap, left: ui.position.left - leftScreenGap };
            }
          },
          revert() {
            if (!originalPosition) {
              return false;
            }
            const { top, left, scrollTop, scrollLeft } = originalPosition;
            $(this).data('uiDraggable').originalPosition = {
              top: top + scrollTop,
              left: left + scrollLeft,
            };
            return true;
          },
          helper: (e) => {
            // The container must be static to prevent unexpected behavior when draggin (absolute)
            // From another parent container
            const formerPosition = e.currentTarget.parentElement.style.position;
            e.currentTarget.parentElement.style.position = 'static';

            // Duplicate the plan-action-item and add it to the bigger container to allow dragging to other plans
            const x = $(e.currentTarget).clone();
            x.find('.chartjs-size-monitor').remove();
            x.find('.editable-label .dark-overlay').remove();
            $clone = $(`<div id="clone" class="plan-action-item ui-draggable-dragging">${x.html()}</div>`);
            $clone.addClass(x.attr('class'));
            $(`#actions-plan-${i}`).append($clone);
            $clone.hide();
            const elementRect = e.currentTarget.getBoundingClientRect();
            originalPosition = {
              left: elementRect.left - containerRect.left,
              top: elementRect.top - containerRect.top,
              scrollLeft: container.scrollLeft,
              scrollTop: container.scrollTop,
            };
            setTimeout(() => {
              $clone.appendTo(container);
              e.currentTarget.parentElement.style.position = formerPosition;
              $clone.show();
            }, 1);
            return $clone;
          },
        });
      });

      // Setup droppable on plan items.
      // Smartphones drop zones first so they take event priority over plan columns
      for (let i = 0; i < plen; i += 1) {
        const aIdsCopy = acceptIds.slice();
        aIdsCopy.splice(acceptIds.indexOf(`#actions-plan-${i} .plan-action-item`), 1);
        const aIds = aIdsCopy.join(',');
        const finalI = i;
        // Drop to switch between plans on smartphone devices
        $(`#${this.smPlanActionId}${this.planActionIdSep}${i}`).droppable({
          accept: aIds,
          drop: (e, ui) => {
            this.handleDrop(e, ui, finalI);
          },
        });
      }
      // All events handled on smartphone devices, do classic events now
      for (let i = 0; i < plen; i += 1) {
        const aIdsCopy = acceptIds.slice();
        aIdsCopy.splice(acceptIds.indexOf(`#actions-plan-${i} .plan-action-item`), 1);
        const aIds = aIdsCopy.join(',');
        const finalI = i;
        // Drop to switch to plan
        $(`#actions-plan-${i}`).droppable({
          accept: aIds,
          drop: (e, ui) => {
            this.handleDrop(e, ui, finalI);
          },
        });
      }
    },
    /** Destroy draggable and droppable handlers (not destroying them would result in memory leaks) */
    destroyDragAndDrop() {
      let instance = $('#plan-drop-zone').droppable('instance');
      if (instance) {
        $('#plan-drop-zone').droppable('destroy');
        $('.plan-backto-pending').droppable('destroy');
        $('.plan-completed').droppable('destroy');
        $('.plan-delete').droppable('destroy');
        for (let i = 0, plen = this.plans.length; i < plen; i++) {
          instance = $(`#actions-plan-${i}`).droppable('instance');
          if (instance) {
            $(`#actions-plan-${i} .plan-action-item`).draggable('destroy');
            $(`#${this.smPlanActionId}${this.planActionIdSep}${i}`).droppable('destroy');
            $(`#actions-plan-${i}`).droppable('destroy');
          }
        }
      }
    },
    handleDrop(e, ui, planIndex) {
      ui.helper.css({ pointerEvents: 'none' });
      const onto = document.elementFromPoint(e.clientX, e.clientY);
      ui.helper.css({ pointerEvents: '' });
      if (onto.id !== 'v-alert' && onto.id !== 'plan-drop-zone') {
        const plan = this.compPlans[planIndex];
        const prevPlan = this.plans.find(p => p.id === Number(ui.draggable[0].dataset.plan));
        const action = prevPlan.actions.find(p => p.id === Number(ui.draggable[0].dataset.id));
        if (action && plan) {
          calert(this.$t('message.ASSIGN_TO_PLAN_CONFIRM', { actionName: action.name, planName: plan.name }), [
            {
              title: this.$t('message.ASSIGN_ACTION'),
              action: () => { this.assign(action, plan, prevPlan); },
            },
            {
              title: this.$t('message.CANCEL'),
            },
          ]);
        }
      }
    },
    beforeShowAI(next) {
      this.iaAnalysisText = IAAnalyser.analysePlans(this, this.plans);
      next();
    },
    createHelpSteps() {
      this.makeDemoValues();
      return [
        new IAHelpStep(null, this.$t('help.ACTIONS_INTRO')),
        new IAHelpStep('.action-plans', this.$t('help.ACTIONS_PLAN')),
        new IAHelpStep('.plan-action-item', this.$t('help.ACTIONS_ITEM')),
        new IAHelpStep('.pdca-container', this.$t('help.ACTIONS_PDCA')),
        new IAHelpStep('.deadline-wrapper', this.$t('help.ACTIONS_DEADLINE')),
        new IAHelpStep('.plan-action-item', this.$t('help.ACTIONS_DRAG'), (() => {
          $('#plan-drop-zone').addClass('no-transition-droppable-active');
        })),
        new IAHelpStep('#plan-drop-zone', this.$t('help.ACTIONS_DROP'), () => {
          $('#plan-drop-zone').removeClass('no-transition-droppable-active');
        },
        [
          {
            text: this.$t('help.ACTIONS_PENDING_LINK'),
            action: () => {
              window.open(this.$router.resolve({ name: 'dmpPending' }).href, '_blank');
            },
          },
        ]),
        new IAHelpStep('.action-plans__archive-all', this.$t('help.ACTIONS_ARCHIVE')),
      ];
    },
    makeDemoValues() {
      this.demoPlans = IAHelp.demoActionPlans(this);
    },
    securingActionClicked(action, actionClicked) {
      if (actionClicked || Number(action.problem) === 0) {
        this.$router.push(this.getActionCreationLink(action.id));
      } else {
        this.$router.push(this.getPbSheetLink(action.problem));
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.action-plans {
  overflow: hidden;
  position: relative;
  white-space: nowrap;

  &__page-title {
    &.basic-sub-title {
      margin-right: 1rem;
    }
  }

  > * {
    white-space: normal;
  }
  h1 {
    position: relative;

    .count {
      font-size: 0.6em;
      position: absolute;
      bottom: 0px;
      right: 10px;
    }
  }

  &__plans-container {
    position: relative;
    display: flex;
    box-sizing: border-box;

    &.f-h-below-filters {
      padding-top: 0.625rem;
    }
  }

  &--as-widget {
    .action-plans__plans-container.f-h-below-filters {
      height: 100%;
    }
  }

  &--as-component {
    background: white;
    border-radius: $main-border-radius;

    .filters-zone__buttons {
      border-bottom: 1px solid $color-gray-border;
      border-top-left-radius: $main-border-radius;
      border-top-right-radius: $main-border-radius;
    }

    .action-plans__plans-container {
      padding: 0px;
    }
  }

  &__plans__plan {
    min-width: $action-item-width;
    margin-right: 0.938rem;
    display: flex;
    flex-flow: column;

    &:last-child {
      margin-right: 0px;
    }

    &--securing {
      margin-right: 0.938rem;

      .basic-blue-title {
        margin-bottom: 0.75rem;
      }
    }

    .plan-action-dropzone {
      position: relative;
      padding-bottom: 5rem;
    }

    &__actions {
      flex: 1;
      min-height: 0px;
      padding: 0.7rem;
      overflow: auto;

      &.ui-droppable-active {
        background: #b0cae885;
        border-radius: 10px;
      }
      &.ui-droppable-hover {
        background: #b0cae8;
      }
    }
  }

  &__plans {
    display: flex;

    &__title-container {
      display: flex;
      align-items: center;
      margin: 1rem 0.625rem 0.5rem 1rem;

      h3 {
        font-size: 1em;
        color: $color-shizen-blue;
        font-weight: 600;
        margin: 0 1rem 0 0;
      }
    }

    span {
      font-size: 1em;
      color: $color-shizen-blue;
    }
  }

  #clone {

    .pdca-container__items {
      display: none;
    }
  }

  .ui-draggable-dragging {
    z-index: 10015;
  }

  .ui-draggable-dragging .plan-action {
    margin-top: 0px;
  }

  .ui-draggable-dragging:hover {
    cursor: grabbing;
  }

  #plan-drop-zone {
    width: 700px;
    position: fixed;
    bottom: -200px;
    left: calc(50% + 110px);
    transform: translateX(-50%);
    z-index: 10005;
    height: 120px;
    font-size: 1rem;
    transition: bottom 0.4s ease-in-out;
    display: flex;
    justify-content: space-between;

    > div {
      border: solid 2px $color-border-grey-dark;
      background: #ffffff;
      border-radius: 5px;
      width: 220px;
      box-sizing: border-box;
      vertical-align: top;
      padding: 20px;

      &.plan-delete {
        border-color: $color-shizen-red;

        &.ui-droppable-hover {
          background-color: $color-shizen-red;
          opacity: 0.8;
        }
      }

      &.plan-completed {
        border-color: $color-shizen-green;

        &.ui-droppable-hover {
          background-color: $color-shizen-green;
          opacity: 0.8;
        }
      }

      &.plan-backto-pending.ui-droppable-hover {
        background: #b0cae8;
      }

      > div {
        margin: auto;
        text-align: center;
      }
    }

    &.ui-droppable-active {
      bottom: 50px;
    }

    &.no-transition-droppable-active {
      transition: none;
      bottom: 30px;
    }
  }

  .archive-all-a {
    background: #048E2B;
    position: fixed;
    bottom: 30px;
    right: 30px;
  }

  &--landscape {

    .action-plans__plans-container {
      flex-direction: column;
    }

    .action-plans__plans {
      display: flex;
      flex-direction: column;

      &__plan {

        .plan-action-dropzone {
          min-height: 4rem;
          display: flex;
          height: initial;
          flex-wrap: wrap;
        }

        &--securing > div {
          display: flex;
          height: initial;
          flex-wrap: wrap;
        }
      }
    }

    .plan-action-item, .action-item {
      margin-right: .8rem;
    }
  }
}


@media (max-width: $smartphone-landscape) {
  .action-plans {

    &__plans-container {
      flex-direction: column;
    }

    &__plans {
      flex-direction: column;
      width: calc(100% - 15px);

      &__plan {
        margin-right: 0;
        margin-top: 1.5rem;

        &--securing {
          min-width: initial;
          max-width: initial;
        }
      }

      &__title-container {
        font-size: 1.3rem;
        margin-bottom: 0.5rem;
      }
    }

    .f-h-below-title {
      height: 100%;
    }

    #plan-drop-zone {
      height: 85px;
      font-size: inherit;
      white-space: nowrap;
      left: calc(#{$smartphone-nav-width / 2} + 15%);

      &.ui-droppable-active {
        bottom: 15px;
      }

      > div {
        padding: 7px;
        border-width: 2px;
        white-space: initial;

        &.plan-delete, &.plan-completed {
          border-width: 2px;
        }
      }
    }

    .archive-all-a {
      width: 35px;
      height: 35px;
      padding: 9px;
      bottom: 10px;
      right: 10px;
    }

    &--landscape {

      .action-plans__plans {
        display: initial !important;

        &__plan {

          .plan-action-dropzone {
            flex-direction: column;
          }

          &--securing > div {
            flex-direction: column;
            height: initial;
          }
        }
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .action-plans {

    #plan-drop-zone {
      width: calc(90%);
      left: calc(#{$smartphone-nav-width / 2} + 5%);
    }
  }
}
</style>
